import {Injectable} from "@angular/core";
import {Observable} from "rxjs";

import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {
    IGlossary,
    IGlossaryEntry,
    ISelection,
    ISentence,
    IExportGlossary,
    IText, IVocabularyEntry, IWordTranslations
} from "../../api/data-model";
import {CssWord, CssSentence} from "../../content/content-view.objects";
import {UtilitiesService} from "../utilities.service";
import {HttpClient} from "@angular/common/http";
import {catchError, map} from "rxjs/operators";
import {PIPELINE_API_STATUS, PipelineSaveEvent} from "../../pipeline/PipelineSaveEvent";
import {EventBus} from "../../event/EventBus";
import {PipelineErrorEvent} from "../../pipeline/PipelineErrorEvent";
import {of} from "rxjs/internal/observable/of";
import {AudioState} from "../../constants";

export interface IEntryResult {
    entry: IGlossaryEntry;
    error: string;
}

export class GlossaryPipelineService {

    constructor(public http: HttpClient,
                private contentServer: string, private logService: BugsnagLoggingService,
                private eventBus: EventBus) {
    }

    get glossary(): IGlossary {
        return this._glossary;
    }

    private _glossary: IGlossary;

    private selectionGlossary = {};

    public loadGlossary(): Observable<string> {
        return this.http.get<IGlossary>(this.contentServer + "glossary" + UtilitiesService.cacheBuster("?"),
            { withCredentials: true })
            .map((response: any) => {
                this._glossary = ((response.entries) as IGlossary);
                this.logService.log("loaded latest glossary");
                return "OK";
            });
    }

    public importGlossary(tsv: any): void {
        let glossaryLines = tsv.split("\n");
        let headers = glossaryLines[0].split("\t");
        headers.forEach((header, index) => {
            headers[index] = header.replace(/\s*\(reverse.*/g, "").trim();
        });
        console.log(headers);
        glossaryLines.shift();
        let parsedGlossary: Array<Array<string>> = [];
        glossaryLines.forEach((line) => {
            parsedGlossary.push(line.trim().split("\t"));
        });
        this.convertGlossaryForImport(headers, parsedGlossary);
        console.log("completed glossary import");
    }

    private convertGlossaryForImport(headers: Array<string>, parsedArray: Array<Array <string>>): void {
        if (!headers || parsedArray.length === 0) {
            console.log("Empty import glossary array!");
            return;
        }
        // depending on either header name or column number is fragile...
        let errorDetail = "";
        let duplicateDetail = "";
        parsedArray.forEach((parsedEntry) => {
            parsedEntry = parsedEntry.map((s) => s.trim());
            if (parsedEntry.some(s => !!s) && !this.usingExistingEntry(headers, parsedEntry)) {
                let glossaryEntry = this.getEntryFromImport(headers, parsedEntry);
                let translation = glossaryEntry.translations.length ? glossaryEntry.translations[0] : null;
                // Not requiring translation due to current process, we may want to revisit later
                if (glossaryEntry.word && glossaryEntry.definition?.text && glossaryEntry.syllables?.text) {
                    let entries = this.getEntry(glossaryEntry.word) ? this.getEntry(glossaryEntry.word) : [];
                    if (!this.isDuplicate(glossaryEntry, entries)) {
                        let id = UtilitiesService.convertWordOrPhraseToID(glossaryEntry.word);
                        let newIndex = entries.length + 1;
                        id += newIndex.toString(10);
                        console.log("adding new entry: " + id);
                        glossaryEntry.id = id;
                        // only Spanish for now
                        if (translation) {
                            translation.id = UtilitiesService.getTranslationId(id, "es");
                        }
                        entries.push(glossaryEntry);
                        this.setEntry(glossaryEntry.word, entries);
                    } else {
                        duplicateDetail += glossaryEntry.word + "\n";
                    }
                } else {
                    errorDetail += parsedEntry.toString() + "\n\n";
                }
            }
        });
        if (errorDetail) {
            window.alert("Some entries not imported due to missing glossary term, translation, or definition.  Detail:\n" + errorDetail);
        }
        if (duplicateDetail) {
            window.alert("Some entries not imported because matching entries already exist for ids:\n" + duplicateDetail);
        }
        // TO DO: posting full glossary for now.  But maybe want to insert entries individually?  Or not?
        this.postGlossary().subscribe((result: string) => {
            console.log("posted updated glossary with result: " + result);
        });
        UtilitiesService.downloadFile(this.reformatGlossary(this._glossary), "glossary.json");
    }

    private usingExistingEntry(headers: Array<string>, entryArray: Array<string>): boolean {

        const index = headers.findIndex(h => h.trim() === "Use Existing Glossary");
        if (index >= 0 && entryArray[index].trim() === "Yes") {
            console.log("skipping import for: " + entryArray);
            return true;
        }
        return false;
    }

    private isDuplicate(newEntry: IGlossaryEntry, existingEntries: IGlossaryEntry[]): boolean {
        // Check for duplicates; can't just do a stringify compare because of auto-generation of id and audio tags
        return !!existingEntries.find(entry => entry.word === newEntry.word &&
            entry.context?.text === newEntry.context?.text && entry.definition?.text === newEntry.definition?.text &&
            entry.fact?.text === newEntry.fact?.text && entry.translations.length &&
            entry.translations[0].language === "es" && entry.translations[0].word === newEntry.translations[0].word &&
            entry.syllables?.text === newEntry.syllables?.text && entry.context?.media === newEntry.context?.media);
    }

    private getEntryFromImport(headers: Array<string>, entryArray: Array<string>): IGlossaryEntry {
        let glossaryEntry: IGlossaryEntry = {
            id: "",
            word: "",
            key: "",
            normalAudio: "",
            audioState: AudioState.UNRECORDED,
            language: "en-US",
            syllables: null,
            definition: null,
            context: {text: "", media: "", audio: "", audioState: AudioState.UNRECORDED},
            translations: []
        };
        console.log("entryArray" + entryArray);
        headers.forEach((header, index) => {
            switch (header) {
                case "Glossary word for import":
                case "Glossary Word":
                case "Glossary word":
                    glossaryEntry.word = entryArray[index];
                    break;
                case "Syll^.a.bles":
                case "Syllables":
                    glossaryEntry.syllables = {text: entryArray[index], audio: "", audioState: AudioState.UNRECORDED, media: ""};
                    break;
                case "Definition":
                    glossaryEntry.definition = {text: entryArray[index], audio: "", audioState: AudioState.UNRECORDED, media: ""};
                    break;
                case "Context":
                case "Context Sentence":
                case "Sentence":
                    glossaryEntry.context.text = entryArray[index];
                    break;
                case "Image filename":
                case "Image Filename":
                    glossaryEntry.context.media = entryArray[index];
                    break;
                case "Translation":
                case "Translation of Glossary Word":
                case "Glossary Word Translated into Spanish (completed by CL)":
                    let wordTrans = entryArray[index];
                    glossaryEntry.translations = [{language: "es", audio: "", audioState: AudioState.UNRECORDED, id: "", word: wordTrans}];
                    break;
                case "Did You Know":
                case "Did You Know (Optional)":
                    glossaryEntry.fact = {text: entryArray[index], audio: "", audioState: AudioState.UNRECORDED, media: ""};
                    break;
                default:
                    console.log("Data not processed: " + header + ": " + entryArray[index]);
                    break;
            }

        });
        return glossaryEntry;
    }

    public getEntry(key: string): IGlossaryEntry[] {
        return (this._glossary && key in this._glossary) ? this._glossary[key].entries : null;
    }

    private setEntry(key: string, entries: IGlossaryEntry[]): void {
        if (this._glossary[key]) {
            this._glossary[key].entries = entries;
        } else {
            this._glossary[key] = {entries};
        }
    }

    public searchGlossary(name: string): Observable<IGlossaryEntry[]> {

        return this.http.get(this.contentServer +
                             "/glossaryLookup?word=" + encodeURIComponent(name), { withCredentials: true })
            .pipe(map((data: any) => {
                let response: IGlossaryEntry[] = data;
                return response;
            }),
            catchError(this.handleError("Search glossary ", null)));
    }

    public saveGlossary(glossaryEntry: IGlossaryEntry): Observable<string> {
        return this.http.put(this.contentServer + "/glossary", glossaryEntry, { withCredentials: true })
            .pipe(map((data) => {
                if (!this._glossary[glossaryEntry.word]) {
                    this._glossary[glossaryEntry.word] = {entries: [glossaryEntry]};
                } else {
                    let entries = this._glossary[glossaryEntry.word].entries;
                    entries = entries.filter((entry) => entry.id !== glossaryEntry.id);
                    entries.push(glossaryEntry);
                    this._glossary[glossaryEntry.word] = {entries};
                }
                return "OK";
            }),
            catchError(this.handleError("Save glossary: '" + glossaryEntry.word + "'", null)));
    }

    public postGlossary(): Observable<string> {
        return this.http.post(this.contentServer + "/glossary", this.reformatGlossary(this._glossary),
                              {headers: {"Content-Type": "application/json", Accept: "application/json"},
                                  withCredentials: true })
            .pipe(map((data) => {
                return "OK";
            }),
            catchError(this.handleError("Save glossary", null)));
    }

    public reformatGlossary(glossary: IGlossary): string {
        // reformat glossary JSON string to match server/Java format
        let glossaryString = JSON.stringify(glossary, null, 2);
        // add space before :
        glossaryString = glossaryString.replace(/": /g, "\" : ");
        // array opening and first object bracket on same line
        glossaryString = glossaryString.replace(/\[\n\s+\{/g, "[ {");
        // array close and last object end bracket on same line
        glossaryString = glossaryString.replace(/}\n\s+]/g, "} ]");
        // object end bracket and next object (in array) open bracket on same line
        glossaryString = glossaryString.replace(/},\n\s+\{/g, "}, {");
        // fix other misc differences
        glossaryString = glossaryString.replace(/"location" : 0,/g, "\"location\" : 0.0,");
        glossaryString = glossaryString.replace(/"imageCredits" : \[],/g, "\"imageCredits\" : [ ],");
        glossaryString = glossaryString.replace(/"translations" : \[]/g, "\"translations\" : [ ]");
        // indents will still not be right, so re-do
        let glossaryStringArray = glossaryString.split(/\n\s+/);
        let indent = 0;
        glossaryStringArray.forEach((line, index) => {
            if (line.indexOf("}") > -1) {
                indent -= 2;
            }
            glossaryStringArray[index] = " ".repeat(indent) + line;
            if (line.indexOf("{") > -1) {
                indent += 2;
            }
        });
        glossaryString = glossaryStringArray.join("\n");
        return glossaryString;
    }

    public deleteGlossary(glossaryEntry: IGlossaryEntry): Observable<string> {
        // this.eventBus.next(new PipelineSaveEvent(PIPELINE_SAVE_STATUS.start));
        return this.http.delete(this.contentServer + "/glossary"
                                + "?glossaryEntry=" + encodeURIComponent(JSON.stringify(glossaryEntry)),
            { withCredentials: true })
            .pipe(map(data => {
                // this.eventBus.next(new PipelineSaveEvent(PIPELINE_SAVE_STATUS.finish));
                return "OK";
            }),
            catchError(this.handleError("Delete glossary", null)));
    }

    private handleError<T>(operation = "operation", result?: T) {
        return (error: any): Observable<T> => {
            console.error(error); // log to console instead

            // TODO: better job of transforming error for user consumption
            const message = `${operation} failed: ${error.message}`;
            console.log(message);

            this.eventBus.next(new PipelineErrorEvent(message));

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    public addGlossaryUsingV1Id(selectionData: ISelection): IExportGlossary {
        // TBD: use data model here when stabilized
        // go through words, look first in local glossary object, then go to glossary itself
        // does translation ever change within a story?  if so, problematic
        // follow existing format but with id as key, and only one entry/one translation
        this.selectionGlossary = {};
        let blocks: IText[] = selectionData.text;
        blocks.forEach((block) => {
            if (block.sentences) {
                this.addGlossaryForBlockUsingV1Id(block.sentences, selectionData.metaData.referenceStory);
            }
        });
        if (selectionData.vocabularyEntries) {
            this.addGlossaryForVocabUsingV1Id(selectionData.vocabularyEntries, selectionData.metaData.referenceStory);
        }

        return this.selectionGlossary;
    }

    private addGlossaryForBlockUsingV1Id(sentences: Array<ISentence>, referenceStory: string): void {
        // TBD: use data model here when stabilized
        // go through words, look first in local glossary object, then go to glossary itself
        // does translation ever change within a story?  if so, problematic
        // follow existing format but with id as key; include all translations.
        sentences.forEach((sentence) => {
            sentence.words.forEach((word) => {
                if (word.glossary) {
                    let glossaryId: string = word.glossary;
                    let result: IEntryResult;
                    let errors = "";
                    result = this.getEntryUsingV1Id(word.glossary);
                    if (result.entry) {
                        errors = result.error + "\n" + this.rationalizeTranslation(word.translations, result.entry);
                        this.selectionGlossary[glossaryId] = result.entry;
                    } else {
                        errors = result.error + "\n" + "missing glossary entry for: " + word.glossary;
                    }
                    if (errors.trim()) {
                        this.logService.log(errors + ": " + referenceStory);
                    }
                }
            });
        });
    }

    private addGlossaryForVocabUsingV1Id(vocabularyEntries: IVocabularyEntry[], referenceStory: string): void {
        vocabularyEntries.forEach((vocabularyTerm) => {
            let glossaryId: string = vocabularyTerm.glossary;
            let result: IEntryResult;
            let errors = "";
            result = this.getEntryUsingV1Id(vocabularyTerm.glossary);
            if (result.entry) {
                errors = result.error + "\n" + this.rationalizeTranslation(vocabularyTerm.translations, result.entry);
                this.selectionGlossary[glossaryId] = result.entry;
            } else {
                errors = result.error + "\n" + "missing glossary entry for: " + vocabularyTerm.glossary;
            }
            if (errors.trim()) {
                this.logService.log(errors + ": " + referenceStory);
            }
        });
    }

    private rationalizeTranslation(translations: IWordTranslations, glossaryEntry: IGlossaryEntry): string {
        let errorString = "";
        Object.keys(translations).forEach((language) => {
            let languageTrans = glossaryEntry.translations.filter((trans) => trans.language === language);
            let translationExists = languageTrans.filter((trans) => {
                                                            return trans.id === translations[language];
                                                        }).length > 0;
            if (!translationExists) {
                if (languageTrans.length === 0) {
                    errorString = "glossary entry: " + glossaryEntry.word + " is missing translations";
                } else {
                    errorString = "missing translation entry: " + translations[language] +
                                  " replaced with: " + languageTrans[0].id;
                    translations[language] = languageTrans[0].id;
                }
            }
        });
        return errorString;
    }

    public getEntryUsingV1Id(id: string): IEntryResult {
        let exportEntry: IGlossaryEntry = null;
        let key: string;
        let errorText = "";
        // for (key in this.glossary) {
        Object.keys(this._glossary).forEach((key) => {
            this._glossary[key].entries.forEach((entry: IGlossaryEntry) => {
                if (entry.id === id) {
                    exportEntry = entry;

                }
            });
        });

        if (!exportEntry) {
            errorText = "no entry found for " + id;
        }

        return {entry: exportEntry, error: errorText};
    }

    public putEntryById(entry: IGlossaryEntry): void {
        if (this._glossary[entry.word]) {
            this._glossary[entry.word].entries = this._glossary[entry.word].entries.filter((e) => {
                return (e.id !== entry.id);
            });
            this._glossary[entry.word].entries.push(entry);
        } else {
            this._glossary[entry.word].entries = [entry];
        }
    }

    public putEntry(entry: IGlossaryEntry): void {
        if (this._glossary[entry.word]) {
            this._glossary[entry.word].entries.push(entry);
        } else {
            this._glossary[entry.word].entries = [entry];
        }
    }

    public getGlossaryTerm(word: CssWord, sentence: CssSentence): string {
        let searchTerm = "";
        if (!word) {
            return;
        }

        while (word.glossary && sentence.previousChild(word) &&
              sentence.previousChild(word).glossary === word.glossary) {
            word = sentence.previousChild(word);
        }
        searchTerm = word.glossary && this.getEntryUsingV1Id(word.glossary).entry ?
            this.getEntryUsingV1Id(word.glossary).entry.word : word.word;
        searchTerm = searchTerm.trim();
        return searchTerm;
    }
}
